import { isDueThisWeek, isOverdue } from './assigned-queue';
import { matchesQuery, selectedAllows } from './directory-filter';
import type { WorkItemSlim } from './work-types';

export const DUE_BUCKETS = ['overdue', 'this_week', 'no_due'] as const;
export type DueBucket = (typeof DUE_BUCKETS)[number];

export function isDueBucket(value: string): value is DueBucket {
  return (DUE_BUCKETS as readonly string[]).includes(value);
}

export function itemMatchesDueBuckets(
  dueDate: string | null,
  selected: string[],
  today: Date,
): boolean {
  if (selected.length === 0) return true;
  return selected.some((bucket) => {
    if (bucket === 'overdue') return isOverdue(dueDate, today);
    if (bucket === 'this_week') return isDueThisWeek(dueDate, today);
    if (bucket === 'no_due') return dueDate == null || dueDate === '';
    return false;
  });
}

export function filterWorkItemRows(
  items: WorkItemSlim[],
  opts: {
    query: string | null;
    types?: string[];
    subtypes?: string[];
    priorities?: string[];
    projectIds?: string[];
    dueBuckets?: string[];
    today?: Date;
    showSubItems?: boolean;
  },
): WorkItemSlim[] {
  const today = opts.today ?? new Date();
  return items.filter((item) => {
    if (opts.showSubItems === false && item.parent_id) return false;
    if (opts.types && !selectedAllows(opts.types, item.type)) return false;
    if (opts.subtypes && !selectedAllows(opts.subtypes, item.subtype ?? '')) return false;
    if (opts.priorities && !selectedAllows(opts.priorities, item.priority)) return false;
    if (opts.projectIds && !selectedAllows(opts.projectIds, item.project_id)) return false;
    if (opts.dueBuckets && !itemMatchesDueBuckets(item.due_date, opts.dueBuckets, today)) {
      return false;
    }
    return matchesQuery(opts.query, [item.title, item.key]);
  });
}

export const WORK_LIST_PAGE_SIZE = 12;
export const WORK_LIST_PAGE_SIZES = [12, 25, 50] as const;

export function paginateItems<T>(
  items: T[],
  page: number,
  pageSize: number,
): { slice: T[]; page: number; pages: number } {
  if (!Number.isInteger(pageSize) || pageSize <= 0) {
    throw new Error('pageSize must be a positive integer.');
  }
  if (!Number.isInteger(page) || page < 1) {
    throw new Error('page must be an integer of 1 or more.');
  }
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pages);
  const start = (safePage - 1) * pageSize;
  return { slice: items.slice(start, start + pageSize), page: safePage, pages };
}
